import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import workOsGroupService from '../services/workOsGroupService';

export const listAllWorkOsGroups = createAsyncThunk('workOsGroup/listAllWorkOsGroups', async () => {
  return workOsGroupService.listAllWorkOsGroups();
});

export const setGroupSystemAdminRoleWithOrg = createAsyncThunk(
  'workOsGroup/setGroupSystemAdminRoleWithOrg',
  async ({ groupId, organizationId }) => {
    return workOsGroupService.setGroupSystemAdminRoleWithOrg(groupId, organizationId);
  }
);

export const createGroupOrgPermission = createAsyncThunk(
  'workOsGroup/createGroupOrgPermission',
  async ({ groupId, permission_level, organizationId }, { rejectWithValue }) => {
    try {
      return await workOsGroupService.createGroupOrgPermission(groupId, permission_level, organizationId);
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const deleteGroupFromOrg = createAsyncThunk('workOsGroup/deleteGroupFromOrg', async ({ groupId, groupRoleId }) => {
  return workOsGroupService.deleteGroupFromOrg(groupId, groupRoleId);
});

export const changeGroupPermissionInOrg = createAsyncThunk(
  'workOsGroup/changeGroupPermissionInOrg',
  async ({ groupId, groupRoleId, newPermissionLevel }) => {
    return workOsGroupService.changeGroupPermissionInOrg(groupId, groupRoleId, newPermissionLevel);
  }
);

const INITIAL_STATE = {
  groups: [],
  isLoading: false,
  error: null
};

export const workOsGroupSlice = createSlice({
  name: 'workOsGroup',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listAllWorkOsGroups.fulfilled, (state, action) => {
        state.groups = action.payload;
      })
      .addCase(listAllWorkOsGroups.pending, (state, action) => {
        state.groups = [];
        state.isLoading = true;
      })
      .addCase(listAllWorkOsGroups.rejected, (state, action) => {
        state.groups = [];
        state.isLoading = false;
        state.error = action.error;
      })
      .addCase(setGroupSystemAdminRoleWithOrg.fulfilled, (state, action) => {
        state.groups = state.groups.map((group) => {
          if (group.id === action.payload.id) {
            const newSuperOrgRole = action.payload.super_organization_role;

            return { ...group, super_organization_role: newSuperOrgRole };
          }
          return group;
        });
      })
      .addCase(setGroupSystemAdminRoleWithOrg.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(setGroupSystemAdminRoleWithOrg.rejected, (state, action) => {
        state.groups = [];
        state.isLoading = false;
        state.error = action.error;
      })
      .addCase(createGroupOrgPermission.fulfilled, (state, action) => {
        const { groupId } = action.meta.arg;

        state.groups = state.groups.map((group) => {
          const updatedGroup = { ...group };

          if (updatedGroup.id === groupId) {
            const { id } = action.payload;
            const { permission_level } = action.payload;
            const organizationId = action.payload.organization;

            updatedGroup.organization_roles = [...updatedGroup.organization_roles, { id, permission_level, organization: organizationId }];
          }
          return updatedGroup;
        });
      })
      .addCase(createGroupOrgPermission.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(createGroupOrgPermission.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteGroupFromOrg.fulfilled, (state, action) => {
        const { groupId } = action.meta.arg;
        const { groupRoleId } = action.meta.arg;

        state.groups = state.groups.map((group) => {
          const updatedGroup = { ...group };

          if (updatedGroup.id === groupId) {
            updatedGroup.organization_roles = updatedGroup.organization_roles.filter((role) => role.id !== groupRoleId);
          }
          return updatedGroup;
        });
        state.isLoading = false;
      })
      .addCase(deleteGroupFromOrg.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteGroupFromOrg.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error;
      })
      .addCase(changeGroupPermissionInOrg.fulfilled, (state, action) => {
        const { groupId } = action.meta.arg;

        state.groups = state.groups.map((group) => {
          const updatedGroup = { ...group };

          if (updatedGroup.id === groupId) {
            updatedGroup.organization_roles = updatedGroup.organization_roles.map((role) => {
              const updatedRole = { ...role };

              if (updatedRole.id === action.payload.id) {
                updatedRole.permission_level = action.payload.permission_level;
              }
              return updatedRole;
            });
          }
          return updatedGroup;
        });
        state.isLoading = false;
      })
      .addCase(changeGroupPermissionInOrg.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(changeGroupPermissionInOrg.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error;
      });
  }
});

export default workOsGroupSlice.reducer;
